/**
 * templateService.js — E3 · plantillas de timeline.
 * El servidor es la única fuente: la plantilla guarda el DOCUMENTO del
 * timeline con huecos y variables; usarla copia ese documento.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TIMELINE_TEMPLATES =
  process.env.REACT_APP_TIMELINE_TEMPLATES !== 'false';

const tl = (projectId, suffix = '') => `${API}/projects/${projectId}/timeline${suffix}`;

export const templateService = {
  async list({ scope = 'mine', category = '', q = '' } = {}) {
    const params = new URLSearchParams({ scope });
    if (category) params.append('category', category);
    if (q) params.append('q', q);
    const { data } = await axios.get(`${API}/templates?${params}`);
    return data;
  },

  async get(templateId) {
    const { data } = await axios.get(`${API}/templates/${templateId}`);
    return data.template;
  },

  async saveAsTemplate(projectId, { meta, slots, textVariables, preview }) {
    const { data } = await axios.post(tl(projectId, '/save-as-template'), {
      meta, slots, text_variables: textVariables, preview,
    });
    return data.template;
  },

  async instantiate(templateId, { projectId, fills = {}, texts = {}, mode = 'replace', baseVersion = null }) {
    const { data } = await axios.post(`${API}/templates/${templateId}/instantiate`, {
      project_id: projectId, fills, texts, mode, base_version: baseVersion,
    });
    return data;
  },

  async patch(templateId, payload) {
    const { data } = await axios.patch(`${API}/templates/${templateId}`, payload);
    return data.template;
  },

  async setPreview(templateId, payload) {
    const { data } = await axios.put(`${API}/templates/${templateId}/preview`, payload);
    return data.template;
  },

  async duplicate(templateId) {
    const { data } = await axios.post(`${API}/templates/${templateId}/duplicate`);
    return data.template;
  },

  async remove(templateId) {
    const { data } = await axios.delete(`${API}/templates/${templateId}`);
    return data;
  },

  async listRenders(projectId) {
    const { data } = await axios.get(tl(projectId, '/renders'));
    return data.renders || [];
  },

  async getSlots(projectId) {
    const { data } = await axios.get(tl(projectId, '/slots'));
    return data;
  },

  async fillSlot(projectId, slotId, asset, baseVersion = null) {
    const { data } = await axios.post(tl(projectId, '/fill-slot'), {
      slot_id: slotId, asset, base_version: baseVersion,
    });
    return data;
  },

  async clearSlot(projectId, slotId, baseVersion = null) {
    const { data } = await axios.post(tl(projectId, '/clear-slot'), {
      slot_id: slotId, base_version: baseVersion,
    });
    return data;
  },

  async setText(projectId, varId, text, baseVersion = null) {
    const { data } = await axios.post(tl(projectId, '/set-text'), {
      var_id: varId, text, base_version: baseVersion,
    });
    return data;
  },
};

/** URL del proxy propio (respaldo cuando R2 todavía no manda CORS). */
export const proxyUrlFor = (url) => {
  const base = (process.env.REACT_APP_R2_PUBLIC_URL || '').replace(/\/$/, '');
  const clean = String(url || '').split('?')[0];
  const key = base && clean.startsWith(base) ? clean.slice(base.length + 1)
    : clean.replace(/^https?:\/\/[^/]+\//, '');
  return `${API}/files/stream?key=${encodeURIComponent(key)}`;
};

const grabOnce = (url, at = 0.3) => new Promise((resolve) => {
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.preload = 'auto';
  video.src = url;
  const fail = () => resolve(null);
  video.onerror = fail;
  video.onloadedmetadata = () => {
    const t = Math.min(Math.max(video.duration * at, 0.05), Math.max(video.duration - 0.05, 0.05));
    video.currentTime = t;
  };
  video.onseeked = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = Math.min(video.videoWidth || 640, 960);
      canvas.height = Math.round(canvas.width * ((video.videoHeight || 360) / (video.videoWidth || 640)));
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.75));
    } catch (_e) {
      resolve(null);   // canvas "tainted": el bucket no manda CORS
    }
  };
  setTimeout(fail, 12000);
});

/**
 * Miniatura desde un MP4, en el NAVEGADOR (sin ffmpeg en el servidor).
 * PRINCIPAL: carga directa desde R2 (necesita CORS en el bucket).
 * RESPALDO: el proxy propio `/api/files/stream`, que sirve el objeto con los
 * CORS de la app. Devuelve `{ dataUrl, via }` — `dataUrl` null si las dos
 * fallan (ahí la UI avisa en vez de quedarse muda).
 */
export const grabFrameFromVideo = async (url, at = 0.3) => {
  const direct = await grabOnce(url, at);
  if (direct) return { dataUrl: direct, via: 'r2' };
  const viaProxy = await grabOnce(proxyUrlFor(url), at);
  return { dataUrl: viaProxy, via: viaProxy ? 'proxy' : null };
};

export default templateService;
